import {
    ProjectGroup,
    Project,
    ProjectStatus,
    User,
    BriefProject,
    DatasetGroup,
    Dataset,
    BriefDataset,
    DatasetItem,
    DatasetStatus,
    DatasetItemStatus,
    DatasetItemInputStatus,
    Organization,
    Tool,
    ProjectTool,
    Grader,
    GraderType,
    ProjectGrader,
    GraderGroup,
    ToolGroup,
    ProjectGraderWithGraderGroup,
    ProjectToolWithToolGroup,
    Implementation,
    PreprocessedData,
    ImplementationCode,
    ToolImplementation,
    Evaluation,
    ApiKey,
    CreatedApiKey,
    DeploymentGroup,
    DeploymentGroupStatus,
    Deployment,
    TraceGroup,
    Trace,
    DeploymentGroupTimeMetricsSchema,
    DeploymentGroupAvgMetricsSchema,
    DeploymentGroupTimeMetricsType,
    Secret,
    SecretType,
    SecretWithValue,
    ToolType,
    EvaluationItem,
    DatasetItemWithEvaluationItems,
    DataCleansingRun,
    DataCleansingRunStatus,
    DataCleansingRunWithResults,
    DataCleansingIssue,
    DataCleansingResult,
    DataCleansingTag,
    ExperimentStageInputAndOutputData,
    ExperimentStageDataWithToolInvocations,
    ToolInvocationWithToolImplementationAndLLMInvocations,
    LlmInvocation,
    LlmInvocationMessage,
    UnpopulatedToolImplementation,
} from 'types/api';
import { JSONSchema7 } from 'json-schema';
import { faker } from '@faker-js/faker';
import { shuffleArray } from 'lib/utils';

// Re-export MSW handlers for easier access
export { getExperimentDataForDatasetItemMSWHandler } from 'features/project/api/get-experiment-data-for-dataset-item';

export const createFakeOrganization = (): Organization => ({
    id: faker.string.uuid(),
    name: faker.company.name(),
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeUser = (): User => ({
    id: faker.string.uuid(),
    name: faker.person.fullName(),
    email: faker.internet.email(),
    organization: createFakeOrganization(),
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
    requires_legal_document_acceptance: false,
    requires_subscription_acceptance: false,
});

export const createFakeProject = ({
    id,
    version = 1,
    datasetId,
    status,
    outputSchema,
    outputDescription,
    recall_requirement,
    projectGroupId,
    rawProblemDescription,
    rawContext,
}: {
    id?: string;
    version?: number;
    datasetId?: string;
    status?: ProjectStatus;
    outputSchema?: JSONSchema7;
    outputDescription?: string;
    recall_requirement?: number;
    projectGroupId?: string;
    rawProblemDescription?: string;
    rawContext?: string;
} = {}): Project => ({
    id: id ?? faker.string.uuid(),
    version,
    project_group_id: projectGroupId ?? faker.string.uuid(),
    evaluation_preferences: {},
    system_designs: [],
    only_use_system_designs: false,
    status:
        status ??
        faker.helpers.arrayElement([
            'PENDING',
            'ACTIVE',
            'INACTIVE',
            'ARCHIVED',
        ] as ProjectStatus[]),
    user: createFakeUser(),
    dataset_id: datasetId,
    raw_problem_description: rawProblemDescription ?? null,
    raw_context: rawContext ?? null,
    maximum_cost: null,
    maximum_latency: null,
    output_schema: outputSchema ?? null,
    output_description: outputDescription ?? null,
    recall_requirement: recall_requirement ?? 1.0,
    maximum_experimentation_cost: 500,
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeBriefProject = (
    version: number,
    projectGroupId?: string,
): BriefProject => ({
    id: faker.string.uuid(),
    project_group_id: projectGroupId ?? faker.string.uuid(),
    version,
    recall_requirement: 1.0,
    evaluation_preferences: {},
    system_designs: [],
    only_use_system_designs: false,
    output_schema: null,
    output_description: null,
    raw_problem_description: null,
    raw_context: null,
    maximum_cost: null,
    maximum_latency: null,
    maximum_experimentation_cost: 500,
    status: faker.helpers.arrayElement([
        'PENDING',
        'ACTIVE',
        'INACTIVE',
        'ARCHIVED',
    ] as ProjectStatus[]),
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeProjectGroup = ({
    numProjects = 1,
    project,
    isArchived = false,
}: {
    numProjects?: number;
    project?: Project;
    isArchived?: boolean;
} = {}): ProjectGroup => {
    if (numProjects < 1) {
        throw new Error('numProjects must be greater than 0');
    }

    const latestProject =
        project ?? createFakeProject({ version: numProjects });
    const projects: BriefProject[] = [];
    for (let version = 1; version < numProjects; version++) {
        const project = createFakeBriefProject(
            version,
            latestProject.project_group_id,
        );
        projects.push(project);
    }
    projects.push({
        ...latestProject,
        version: numProjects,
    });

    return {
        id: latestProject.project_group_id,
        name: faker.company.buzzPhrase(),
        description: faker.lorem.paragraph(),
        created_at: faker.date.past(),
        updated_at: faker.date.recent(),
        is_archived: isArchived,
        user: createFakeUser(),
        projects,
    };
};

export const createFakeDatasetGroup = ({
    name,
    description,
    latestDataset,
    datasetVersion,
}: {
    name?: string;
    description?: string;
    latestDataset?: Dataset;
    datasetVersion?: number;
} = {}): DatasetGroup => {
    latestDataset =
        latestDataset ?? createFakeDataset({ version: datasetVersion ?? 2 });
    const id = latestDataset.dataset_group_id;
    const datasets: BriefDataset[] = [];
    datasets.push(latestDataset);
    for (let version = latestDataset.version - 1; version > 0; version--) {
        datasets.push(createFakeBriefDataset(version));
    }

    return {
        id,
        name: name ?? faker.company.buzzPhrase(),
        description: description ?? faker.lorem.paragraph(),
        datasets,
        created_by_user: createFakeUser(),
        created_at: faker.date.past(),
        updated_at: faker.date.recent(),
        is_archived: false,
    };
};

export const createFakeDataset = ({
    version = 1,
    inputSchema,
    outputSchema,
    isLatestVersion = Math.random() > 0.5,
    withoutOutput = false,
}: {
    version?: number;
    inputSchema?: JSONSchema7;
    outputSchema?: JSONSchema7;
    isLatestVersion?: boolean;
    withoutOutput?: boolean;
} = {}): Dataset => {
    const id = faker.string.uuid();
    return {
        id,
        version,
        infos: [
            {
                id: faker.string.uuid(),
                dataset_id: id,
                variable_name: 'sentence',
                special_data_type: null,
                created_at: faker.date.past(),
                updated_at: faker.date.recent(),
            },
        ],
        input_schema: inputSchema ?? {
            type: 'object',
            properties: {
                sentence: {
                    description: 'A sentence',
                    type: 'string',
                },
            },
            additionalProperties: false,
        },
        output_schema: withoutOutput
            ? undefined
            : (outputSchema ?? {
                  type: 'object',
                  properties: {
                      is_positive: {
                          description: 'Is the sentence positive?',
                          type: 'boolean',
                      },
                  },
                  additionalProperties: false,
              }),
        created_at: faker.date.past(),
        updated_at: faker.date.recent(),
        is_latest_version: isLatestVersion,
        dataset_group_id: faker.string.uuid(),
        status: DatasetStatus.PROCESSED,
    };
};

export const createFakeBriefDataset = (
    version = 1,
    id?: string,
): BriefDataset => ({
    id: id ?? faker.string.uuid(),
    version,
    status: DatasetStatus.PROCESSED,
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeDatasetItem = (
    datasetId: string,
    withErrors: boolean = false,
    rawData: boolean = false,
): DatasetItem => ({
    id: faker.string.uuid(),
    dataset_id: datasetId,
    inputs: [
        {
            id: faker.string.uuid(),
            info: {
                id: faker.string.uuid(),
                dataset_id: datasetId,
                variable_name: 'sentence',
                special_data_type: null,
                created_at: faker.date.past(),
                updated_at: faker.date.recent(),
            },
            data: withErrors
                ? Math.random() > 0.5
                    ? null
                    : faker.lorem.paragraph()
                : faker.lorem.paragraph(),
            raw_data: rawData ? faker.lorem.sentence() : null,
            status: rawData
                ? DatasetItemInputStatus.PENDING
                : DatasetItemInputStatus.PROCESSED,
            document: null,
            user_facing_error: withErrors ? 'Processing error' : null,
            created_at: faker.date.past(),
            updated_at: faker.date.recent(),
        },
    ],
    output: faker.datatype.boolean(),
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
    status: rawData
        ? DatasetItemStatus.PROCESSING
        : withErrors
          ? DatasetItemStatus.FAILED
          : DatasetItemStatus.PROCESSED,
});

export const createFakeToolGroup = ({
    latestTool,
    user,
    organizationId = faker.string.uuid(),
}: {
    latestTool?: Tool;
    user?: User;
    organizationId?: string | null;
} = {}): ToolGroup => {
    const id = latestTool ? latestTool.tool_group_id : faker.string.uuid();
    const tools: Tool[] = [];
    if (latestTool) {
        tools.push(latestTool);
    }
    return {
        id,
        organization_id: organizationId,
        name: faker.company.buzzPhrase(),
        type: ToolType.LLM,
        created_by_user: user ?? createFakeUser(),
        tools,
        is_archived: false,
        created_at: faker.date.past(),
        updated_at: faker.date.recent(),
        is_editable: false,
    };
};

export const createFakeTool = ({
    toolGroupId,
    version = 1,
    base_config,
    config_schema,
}: {
    toolGroupId?: string;
    version?: number;
    base_config?: Tool['base_config'];
    config_schema?: Tool['config_schema'];
} & (
    | { base_config: Tool['base_config']; config_schema: Tool['config_schema'] }
    | { base_config?: undefined; config_schema?: undefined }
) = {}): Tool => ({
    id: faker.string.uuid(),
    version,
    tool_group_id: toolGroupId ?? faker.string.uuid(),
    description: faker.lorem.paragraph(),
    base_config: base_config ?? {
        allowed_models: ['gpt-4o-mini', 'claude-3-5-sonnet', 'claude-3-haiku'],
    },
    config_schema: config_schema ?? {
        type: 'object',
        properties: {
            allowed_models: {
                type: 'array',
                items: {
                    type: 'string',
                    enum: [
                        'gpt-4o',
                        'gpt-4o-mini',
                        'claude-3-5-sonnet',
                        'claude-3-haiku',
                        'Anthropic claude-3-7-sonnet-20250219 (thinking)',
                    ],
                },
            },
        },
        additionalProperties: false,
        required: ['allowed_models'],
    },
    allow_multiple_per_project: false,
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeProjectTool = (config?: {
    schema: JSONSchema7;
    base_config: Record<string, unknown>;
    values: Record<string, unknown>;
}): ProjectTool => {
    let tool = createFakeTool();
    if (config) {
        tool = createFakeTool({
            config_schema: config.schema,
            base_config: config.base_config,
        });
    }
    return {
        id: faker.string.uuid(),
        project_id: faker.string.uuid(),
        tool,
        name: faker.company.buzzPhrase(),
        config: config?.values ?? {
            allowed_models: ['gpt-4o-mini', 'claude-3-5-sonnet'],
        },
        created_at: faker.date.past(),
        updated_at: faker.date.recent(),
    };
};

export const createFakeProjectToolWithToolGroup = (config?: {
    schema: JSONSchema7;
    base_config: Record<string, unknown>;
    values: Record<string, unknown>;
}): ProjectToolWithToolGroup => {
    const projectTool = createFakeProjectTool(config);
    const toolGroup = createFakeToolGroup({ latestTool: projectTool.tool });
    return {
        ...projectTool,
        tool_group: toolGroup,
        is_latest_version: Math.random() > 0.5,
    };
};

export const createFakeGraderGroup = (latestGrader?: Grader): GraderGroup => {
    const id = faker.string.uuid();
    const graders = Array.from({ length: 5 }, () =>
        createFakeGrader({ graderGroupId: id }),
    );
    if (latestGrader) {
        graders.push(latestGrader);
    }
    return {
        id,
        organization_id: Math.random() > 0.5 ? null : faker.string.uuid(),
        name: faker.company.buzzPhrase(),
        graders,
        precision_explanation:
            Math.random() > 0.5 ? faker.lorem.paragraph() : undefined,
        recall_explanation:
            Math.random() > 0.5 ? faker.lorem.paragraph() : undefined,
        created_by_user: createFakeUser(),
        is_archived: false,
        is_editable: Math.random() > 0.5,
        created_at: faker.date.past(),
        updated_at: faker.date.recent(),
    };
};

export const createFakeGrader = ({
    type = GraderType.CODE,
    config_schema,
    graderGroupId,
    version = 1,
}: {
    type?: GraderType;
    config_schema?: JSONSchema7;
    graderGroupId?: string;
    version?: number;
} = {}): Grader => {
    const graderBase: Grader = {
        id: faker.string.uuid(),
        version,
        grader_group_id: graderGroupId ?? faker.string.uuid(),
        type,
        description: faker.lorem.paragraph(),
        prompt_criteria:
            type === GraderType.PROMPT ? faker.lorem.paragraph() : undefined,
        config_schema: config_schema ?? undefined,
        allow_multiple_per_project: Math.random() > 0.5,
        created_at: faker.date.past(),
        updated_at: faker.date.recent(),
    };
    if (type === GraderType.CODE) {
        return {
            ...graderBase,
            code: `def evaluate(actual, expected):\n    return actual == expected`,
        };
    } else if (type === GraderType.PROMPT) {
        return {
            ...graderBase,
            prompt_criteria: faker.lorem.paragraph(),
        };
    }
    return graderBase;
};

export const createFakeProjectGrader = (config?: {
    schema?: JSONSchema7;
    values?: Record<string, unknown>;
}): ProjectGrader => ({
    id: faker.string.uuid(),
    project_id: faker.string.uuid(),
    name: faker.company.buzzPhrase(),
    grader: createFakeGrader({
        type: Math.random() > 0.5 ? GraderType.CODE : GraderType.PROMPT,
        config_schema: config?.schema,
    }),
    minimum_precision: 0.5,
    minimum_recall: 0.5,
    config: config?.values ?? {},
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeProjectGraderWithGraderGroup = (config?: {
    schema?: JSONSchema7;
    values?: Record<string, unknown>;
}): ProjectGraderWithGraderGroup => {
    const projectGrader = createFakeProjectGrader(config);
    const graderGroup = createFakeGraderGroup(projectGrader.grader);
    return {
        ...projectGrader,
        grader_group: graderGroup,
        is_latest_version: Math.random() > 0.5,
    };
};

export const createFakeImplementationCode = (): ImplementationCode => ({
    id: faker.string.uuid(),
    name: faker.company.buzzPhrase(),
    code: `def process_data(data):
    return data['sentence']`,
    input_variable_names: [
        faker.lorem.word(),
        faker.lorem.word(),
        faker.lorem.word(),
    ],
    output_variable_name: faker.lorem.word(),
    description: faker.lorem.sentence(),
    order: 0,
    code_history: [
        {
            code: faker.lorem.paragraph(),
            created_at: faker.date.past(),
        },
    ],
    experiment_stage_id: faker.string.uuid(),
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakePreprocessedData = (
    data?: unknown,
): PreprocessedData => ({
    id: faker.string.uuid(),
    variable_name: faker.lorem.word(),
    description: faker.lorem.sentence(),
    data: data ?? faker.lorem.paragraph(),
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeToolImplementation = ({
    thinking_enabled = Math.random() > 0.5,
    reasoning_effort = Math.random() > 0.5 ? 'low' : 'medium',
    max_thinking_tokens = Math.random() > 0.5 ? 1024 : 3000,
}: {
    thinking_enabled?: boolean | null;
    reasoning_effort?: 'low' | 'medium' | 'high' | null;
    max_thinking_tokens?: number | null;
}): ToolImplementation => ({
    function_name: faker.lorem.word(),
    description: faker.lorem.sentence(),
    output_schema: { type: 'string' },
    implementation_information: {
        system_prompt: faker.lorem.paragraph(),
        user_message_template: faker.lorem.paragraph(),
        output_format: 'JSON',
        json_schema: {
            type: 'object',
            properties: {
                sentiment: {
                    type: 'string',
                    enum: ['positive', 'negative', 'neutral'],
                },
            },
        },
        model: 'gpt-4o-mini',
        temperature: 0.5,
        cost: 0.01,
        latency: 1000,
        inference_arguments: ['sentence'],
        thinking_enabled: thinking_enabled,
        reasoning_effort: reasoning_effort,
        max_thinking_tokens: max_thinking_tokens,
    },
    project_tool: createFakeProjectToolWithToolGroup(),
});

export const createFakeEvaluationItem = (): EvaluationItem => ({
    id: faker.string.uuid(),
    evaluation_id: faker.string.uuid(),
    dataset_item_id: faker.string.uuid(),
    precision: Math.random(),
    recall: Math.random(),
    skip: faker.datatype.boolean(),
    explanation: faker.lorem.sentence(),
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeDatasetItemWithEvaluationItems =
    (): DatasetItemWithEvaluationItems => ({
        ...createFakeDatasetItem(faker.string.uuid()),
        evaluation_items: Array.from({ length: 3 }, createFakeEvaluationItem),
    });

export const createFakeEvaluation = (): Evaluation => {
    const numItemsWithPrecisionGraded = faker.number.int({ min: 1, max: 10 });
    const numItemsWithRecallGraded = faker.number.int({ min: 1, max: 10 });
    return {
        id: faker.string.uuid(),
        avg_precision: Math.random(),
        avg_recall: Math.random(),
        project_grader: createFakeProjectGraderWithGraderGroup(),
        any_answer_recall: Math.random(),
        num_items_with_precision_graded: numItemsWithPrecisionGraded,
        num_items_with_recall_graded: numItemsWithRecallGraded,
        items_with_precision_graded_aggregate_score:
            Math.random() * numItemsWithPrecisionGraded,
        items_with_recall_graded_aggregate_score:
            Math.random() * numItemsWithRecallGraded,
        created_at: faker.date.past(),
        updated_at: faker.date.recent(),
    };
};

export const createFakeImplementation = ({
    numImplementationCodes = 3,
    numPreprocessedData = 1,
    numTools = 1,
    numEvaluations = 2,
    cost,
    latency,
    thinking_enabled,
    reasoning_effort,
    max_thinking_tokens,
}: {
    numImplementationCodes?: number;
    numPreprocessedData?: number;
    numTools?: number;
    numEvaluations?: number;
    cost?: number | null;
    latency?: number | null;
    thinking_enabled?: boolean | null;
    reasoning_effort?: 'low' | 'medium' | 'high' | null;
    max_thinking_tokens?: number | null;
} = {}): Implementation => ({
    id: faker.string.uuid(),
    name: faker.company.buzzPhrase(),
    input_schema: {
        type: 'object',
        properties: {
            sentence: {
                description: 'A sentence',
                type: 'string',
            },
        },
        additionalProperties: false,
    },
    output_schema: { type: 'string' },
    implementation_codes: Array.from({ length: numImplementationCodes }, () =>
        createFakeImplementationCode(),
    ),
    preprocessed_data: Array.from({ length: numPreprocessedData }, () =>
        createFakePreprocessedData(),
    ),
    tools: Array.from({ length: numTools }, () =>
        createFakeToolImplementation({
            thinking_enabled,
            reasoning_effort,
            max_thinking_tokens,
        }),
    ),
    evaluations: Array.from({ length: numEvaluations }, () =>
        createFakeEvaluation(),
    ),
    cost: cost === null ? null : Math.random() * 5,
    latency: latency === null ? null : Math.random() * 20,
    completed_at: faker.date.recent(),
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeApiKey = (): ApiKey => ({
    id: faker.string.uuid(),
    name: faker.company.buzzPhrase(),
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
    key_prefix: faker.string.uuid().slice(0, 4),
    key_suffix: faker.string.uuid().slice(0, 4),
    status: Math.random() > 0.5 ? 'enabled' : 'disabled',
    created_by_user: createFakeUser(),
    last_used_at: faker.date.recent(),
});

export const createFakeCreatedApiKey = (): CreatedApiKey => ({
    api_key: faker.string.uuid(),
    ...createFakeApiKey(),
});

export const createFakeDeploymentGroup = (): DeploymentGroup => ({
    id: faker.string.uuid(),
    name: faker.company.buzzPhrase(),
    status:
        Math.random() > 0.5
            ? DeploymentGroupStatus.ENABLED
            : DeploymentGroupStatus.DISABLED,
    validate_input: Math.random() > 0.5,
    validate_output: Math.random() > 0.5,
    created_by_user: createFakeUser(),
    deployments: Array.from({ length: 3 }, () => createFakeDeployment()),
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeDeployment = (): Deployment => ({
    id: faker.string.uuid(),
    deployment_group_id: faker.string.uuid(),
    rollout_percentage: Math.random() * 10000,
    version: 1,
    implementation_id: faker.string.uuid(),
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeTrace = (): Trace => ({
    id: faker.string.uuid(),
    trace_group_id: faker.string.uuid(),
    start_time: faker.date.past(),
    end_time: faker.date.recent(),
    latency: Math.random() * 1000,
    cents_cost: Math.random() * 100,
    input_data: {
        sentence: faker.lorem.sentence(),
    },
    output_data: faker.lorem.sentence(),
    error: null,
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
    attributes: {},
});

export const createFakeDeploymentGroupTimeMetricsSchema = (
    interval: DeploymentGroupTimeMetricsSchema['interval'],
    type: DeploymentGroupTimeMetricsType = DeploymentGroupTimeMetricsType.COST,
): DeploymentGroupTimeMetricsSchema => {
    const now = new Date();
    let startDate: Date;
    let timestamps: Date[];

    switch (interval) {
        case 'minute':
            // Last hour, 10 minute intervals
            startDate = new Date(now.getTime() - 60 * 60 * 1000); // 1 hour ago
            timestamps = Array.from({ length: 10 }, (_, i) => {
                const date = new Date(startDate.getTime() + i * 6 * 60 * 1000);
                date.setSeconds(0, 0); // Zero out seconds and milliseconds
                return date;
            });
            break;

        case 'hour':
            // Last day, hourly intervals
            startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000); // 24 hours ago
            timestamps = Array.from({ length: 10 }, (_, i) => {
                const date = new Date(
                    startDate.getTime() + i * 2.4 * 60 * 60 * 1000,
                );
                date.setMinutes(0, 0, 0); // Zero out minutes, seconds, and milliseconds
                return date;
            });
            break;

        case 'day':
            // Last month, daily intervals
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
            timestamps = Array.from({ length: 10 }, (_, i) => {
                const date = new Date(
                    startDate.getTime() + i * 3 * 24 * 60 * 60 * 1000,
                );
                date.setHours(0, 0, 0, 0); // Zero out hours, minutes, seconds, and milliseconds
                return date;
            });
            break;

        case 'week':
            // Last 10 weeks, weekly intervals
            startDate = new Date(now.getTime() - 10 * 7 * 24 * 60 * 60 * 1000); // 10 weeks ago
            timestamps = Array.from({ length: 10 }, (_, i) => {
                const date = new Date(
                    startDate.getTime() + i * 7 * 24 * 60 * 60 * 1000,
                );
                date.setHours(0, 0, 0, 0);
                date.setDate(date.getDate() - date.getDay()); // Set to start of week
                return date;
            });
            break;

        case 'month':
            // Last 10 months, monthly intervals
            startDate = new Date(now.getTime() - 10 * 30 * 24 * 60 * 60 * 1000); // ~10 months ago
            timestamps = Array.from({ length: 10 }, (_, i) => {
                const date = new Date(startDate.getTime());
                date.setMonth(date.getMonth() + i);
                date.setDate(1);
                date.setHours(0, 0, 0, 0);
                return date;
            });
            break;
    }

    return {
        data: timestamps.map((timestamp) => ({
            timestamp,
            value:
                type === DeploymentGroupTimeMetricsType.COST
                    ? Math.random() * 100
                    : Math.floor(Math.random() * 100),
        })),
        interval,
        type,
    };
};

export const createFakeDeploymentGroupAvgMetricsSchema =
    (): DeploymentGroupAvgMetricsSchema => ({
        avg_cost: Math.random() * 100,
        avg_latency: Math.random() * 100,
    });

export const createFakeTraceGroup = (): TraceGroup => ({
    id: faker.string.uuid(),
    name: faker.company.buzzPhrase(),
    description: faker.lorem.sentence(),
    sample_rate: Math.random() * 10000,
    sampling_percentage: Math.random(),
    deployment: Math.random() > 0.5 ? createFakeDeployment() : null,
    created_by_user: createFakeUser(),
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeSecret = ({
    type = SecretType.CUSTOM,
}: {
    type?: SecretType;
} = {}): Secret => ({
    id: faker.string.uuid(),
    name: faker.company.buzzPhrase(),
    organization_id: faker.string.uuid(),
    type: type,
    created_by_user: createFakeUser(),
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeSecretWithValue = (): SecretWithValue => ({
    ...createFakeSecret(),
    value: faker.string.uuid(),
});

export const createFakeDataCleansingRun = ({
    status = DataCleansingRunStatus.COMPLETED,
    totalItems = faker.number.int({ min: 10, max: 100 }),
    itemsWithIssues = faker.number.int({ min: 0, max: totalItems }),
    projectId,
    datasetId,
}: {
    status?: DataCleansingRunStatus;
    totalItems?: number;
    itemsWithIssues?: number;
    projectId?: string;
    datasetId?: string;
} = {}): DataCleansingRun => ({
    id: faker.string.uuid(),
    dataset_id: datasetId ?? faker.string.uuid(),
    project_id: projectId ?? faker.string.uuid(),
    status,
    started_at: faker.date.past(),
    completed_at:
        status === DataCleansingRunStatus.COMPLETED
            ? faker.date.recent()
            : null,
    total_items: totalItems,
    items_with_issues: itemsWithIssues,
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeDataCleansingIssue = ({
    resultId,
    tagId,
    isResolved = faker.datatype.boolean(),
    description,
    comments,
}: {
    resultId?: string;
    tagId?: string | null;
    isResolved?: boolean;
    description?: string;
    comments?: string | null;
} = {}): DataCleansingIssue => ({
    id: faker.string.uuid(),
    result_id: resultId ?? faker.string.uuid(),
    description:
        description ??
        faker.helpers.arrayElement([
            'Output label contradicts input context',
            'Inconsistent labeling compared to similar examples',
            'Missing required information in output',
            'Ambiguous input data that cannot be accurately labeled',
            'Output format does not match schema requirements',
            'Classification confidence is too low for reliable training',
        ]),
    comments: comments ?? (Math.random() > 0.7 ? faker.lorem.sentence() : null),
    tag_id:
        tagId !== undefined
            ? tagId
            : Math.random() > 0.6
              ? faker.string.uuid()
              : null,
    is_resolved: isResolved,
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeDataCleansingTag = ({
    runId,
    name,
    description,
    isResolved = faker.datatype.boolean(),
    comments,
}: {
    runId?: string;
    name?: string;
    description?: string;
    isResolved?: boolean;
    comments?: string | null;
} = {}): DataCleansingTag => ({
    id: faker.string.uuid(),
    run_id: runId ?? faker.string.uuid(),
    name:
        name ??
        faker.helpers.arrayElement([
            'Label Inconsistency',
            'Context Mismatch',
            'Schema Violation',
            'Ambiguous Data',
            'Low Confidence',
            'Format Error',
        ]),
    description:
        description ??
        faker.helpers.arrayElement([
            'Multiple examples with conflicting labels for similar inputs',
            "Output labels don't align with provided context and requirements",
            "Data doesn't conform to the expected output schema",
            'Input data is too ambiguous to label consistently',
            'Model confidence scores are below acceptable threshold',
            "Output format doesn't match the required structure",
        ]),
    is_resolved: isResolved,
    comments: comments ?? (Math.random() > 0.6 ? faker.lorem.sentence() : null),
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
});

export const createFakeDatasetItemForDataCleansing = (
    datasetId: string,
): DatasetItem => ({
    id: faker.string.uuid(),
    dataset_id: datasetId,
    inputs: [
        {
            id: faker.string.uuid(),
            info: {
                id: faker.string.uuid(),
                dataset_id: datasetId,
                variable_name: 'sentence',
                special_data_type: null,
                created_at: faker.date.past(),
                updated_at: faker.date.recent(),
            },
            data: faker.lorem.sentence(),
            raw_data: null,
            status: DatasetItemInputStatus.PROCESSED,
            document: null,
            user_facing_error: null,
            created_at: faker.date.past(),
            updated_at: faker.date.recent(),
        },
        {
            id: faker.string.uuid(),
            info: {
                id: faker.string.uuid(),
                dataset_id: datasetId,
                variable_name: 'context',
                special_data_type: null,
                created_at: faker.date.past(),
                updated_at: faker.date.recent(),
            },
            data: faker.lorem.paragraph(2),
            raw_data: null,
            status: DatasetItemInputStatus.PROCESSED,
            document: null,
            user_facing_error: null,
            created_at: faker.date.past(),
            updated_at: faker.date.recent(),
        },
    ],
    output: {
        classification: faker.helpers.arrayElement([
            'positive',
            'negative',
            'neutral',
        ]),
        confidence: faker.number.float({
            min: 0.1,
            max: 1.0,
            multipleOf: 0.01,
        }),
    },
    metadata: {
        source: faker.helpers.arrayElement(['manual', 'generated', 'imported']),
        annotator: faker.person.fullName(),
        timestamp: faker.date.past().toISOString(),
    },
    created_at: faker.date.past(),
    updated_at: faker.date.recent(),
    status: DatasetItemStatus.PROCESSED,
});

// Export DataCleansingRunStatus for use in stories
export { DataCleansingRunStatus };

export const createFakeDataCleansingResult = ({
    runId,
    datasetItem,
    isCorrect = faker.datatype.boolean(),
    numIssues = isCorrect ? 0 : faker.number.int({ min: 1, max: 4 }),
    tags,
    percentResolvedIssues = 0.5,
}: {
    runId?: string;
    datasetItem?: DatasetItem;
    isCorrect?: boolean;
    numIssues?: number;
    tags?: DataCleansingTag[];
    percentResolvedIssues?: number;
} = {}): DataCleansingResult => {
    const result: DataCleansingResult = {
        id: faker.string.uuid(),
        run_id: runId ?? faker.string.uuid(),
        dataset_item_id: datasetItem?.id ?? faker.string.uuid(),
        dataset_item:
            datasetItem ??
            createFakeDatasetItemForDataCleansing(faker.string.uuid()),
        is_correct: isCorrect,
        subtask_count: faker.number.int({ min: 1, max: 5 }),
        issues: [],
        created_at: faker.date.past(),
        updated_at: faker.date.recent(),
    };

    // Create issues for this result
    const availableTags = tags || [];
    for (let i = 0; i < numIssues; i++) {
        const tagId =
            availableTags.length > 0 && Math.random() > 0.4
                ? faker.helpers.arrayElement(availableTags).id
                : null;

        result.issues.push(
            createFakeDataCleansingIssue({
                resultId: result.id,
                tagId,
                isResolved: Math.random() < percentResolvedIssues,
            }),
        );
    }

    return result;
};

export const createFakeDataCleansingRunWithResults = ({
    status = DataCleansingRunStatus.COMPLETED,
    numResults = faker.number.int({ min: 5, max: 20 }),
    numCorrectResults = faker.number.int({ min: 0, max: numResults }),
    numTags = faker.number.int({ min: 2, max: 6 }),
    percentResolvedIssues = 0.5,
    projectId,
    datasetId,
}: {
    status?: DataCleansingRunStatus;
    numResults?: number;
    numCorrectResults?: number;
    numTags?: number;
    percentResolvedIssues?: number;
    projectId?: string;
    datasetId?: string;
} = {}): DataCleansingRunWithResults => {
    const numIncorrectResults = numResults - numCorrectResults;
    const runId = faker.string.uuid();

    // Create tags first
    const tags = Array.from({ length: numTags }, () =>
        createFakeDataCleansingTag({ runId }),
    );

    const correctResults = Array.from({ length: numCorrectResults }, () => {
        const datasetItem = createFakeDatasetItemForDataCleansing(
            datasetId ?? faker.string.uuid(),
        );
        return createFakeDataCleansingResult({
            runId,
            datasetItem,
            tags,
            percentResolvedIssues,
        });
    });

    const incorrectResults = Array.from({ length: numIncorrectResults }, () => {
        const datasetItem = createFakeDatasetItemForDataCleansing(
            datasetId ?? faker.string.uuid(),
        );
        return createFakeDataCleansingResult({
            runId,
            datasetItem,
            tags,
            percentResolvedIssues,
        });
    });

    const results = [...correctResults, ...incorrectResults];
    shuffleArray(results);

    const totalItemsWithIssues = results.filter((r) => !r.is_correct).length;

    return {
        id: runId,
        dataset_id: datasetId ?? faker.string.uuid(),
        project_id: projectId ?? faker.string.uuid(),
        status,
        started_at: faker.date.past(),
        completed_at:
            status === DataCleansingRunStatus.COMPLETED
                ? faker.date.recent()
                : null,
        total_items: numResults,
        items_with_issues: totalItemsWithIssues,
        results,
        tags,
        created_at: faker.date.past(),
        updated_at: faker.date.recent(),
    };
};

// Mock data functions for experiment stage data
export const createFakeLlmInvocationMessage = ({
    llmInvocationId,
    role = faker.helpers.arrayElement(['system', 'user', 'assistant']),
    order = 0,
}: {
    llmInvocationId?: string;
    role?: string;
    order?: number;
} = {}): LlmInvocationMessage => ({
    id: faker.string.uuid(),
    llm_invocation_id: llmInvocationId ?? faker.string.uuid(),
    template:
        role === 'system'
            ? 'You are a helpful AI assistant specialized in {task_type}. Please analyze the following input and provide {output_format}.'
            : role === 'user'
              ? 'Please analyze this data: {input_data}'
              : faker.lorem.paragraph(),
    populated_message:
        role === 'system'
            ? 'You are a helpful AI assistant specialized in sentiment analysis. Please analyze the following input and provide JSON formatted response.'
            : role === 'user'
              ? `Please analyze this data: "${faker.lorem.sentence()}"`
              : faker.lorem.paragraph(3),
    role,
    input_variables: {
        task_type: 'sentiment analysis',
        output_format: 'JSON formatted response',
        input_data: faker.lorem.sentence(),
    },
    order,
    created_at: faker.date.past(),
});

export const createFakeLlmInvocation = ({
    toolInvocationId,
}: {
    toolInvocationId?: string;
} = {}): LlmInvocation => {
    const id = faker.string.uuid();
    const messages: LlmInvocationMessage[] = [];

    // Create system, user, and assistant messages
    messages.push(
        createFakeLlmInvocationMessage({
            llmInvocationId: id,
            role: 'system',
            order: 0,
        }),
    );
    messages.push(
        createFakeLlmInvocationMessage({
            llmInvocationId: id,
            role: 'user',
            order: 1,
        }),
    );
    messages.push(
        createFakeLlmInvocationMessage({
            llmInvocationId: id,
            role: 'assistant',
            order: 2,
        }),
    );

    return {
        id,
        tool_invocation_id: toolInvocationId ?? faker.string.uuid(),
        model: faker.helpers.arrayElement([
            'gpt-4o-mini',
            'claude-3-5-sonnet',
            'claude-3-haiku',
            'gpt-4o',
        ]),
        provider: faker.helpers.arrayElement(['openai', 'anthropic']),
        temperature: faker.number.float({ min: 0, max: 1, multipleOf: 0.1 }),
        input_tokens: faker.number.int({ min: 100, max: 2000 }),
        output_tokens: faker.number.int({ min: 50, max: 1000 }),
        latency_seconds: faker.number.float({
            min: 0.5,
            max: 10,
            multipleOf: 0.1,
        }),
        cents_cost: faker.number.float({
            min: 0.01,
            max: 5.0,
            multipleOf: 0.01,
        }),
        messages,
        created_at: faker.date.past(),
        updated_at: faker.date.recent(),
    };
};

export const createFakeUnpopulatedToolImplementation =
    (): UnpopulatedToolImplementation => ({
        id: faker.string.uuid(),
        project_tool_id: faker.string.uuid(),
        function_name: faker.helpers.arrayElement([
            'analyze_sentiment',
            'extract_entities',
            'classify_text',
            'summarize_content',
            'generate_response',
        ]),
        description: faker.lorem.sentence(),
        output_schema: {
            type: 'object',
            properties: {
                result: { type: 'string' },
                confidence: { type: 'number' },
            },
        },
        implementation_information: {
            system_prompt: faker.lorem.paragraph(),
            user_message_template: faker.lorem.sentence(),
            output_format: faker.helpers.arrayElement([
                'JSON',
                'STRING',
                'ARRAY',
            ]),
            json_schema: {
                type: 'object',
                properties: {
                    sentiment: {
                        type: 'string',
                        enum: ['positive', 'negative', 'neutral'],
                    },
                },
            },
            model: faker.helpers.arrayElement([
                'gpt-4o-mini',
                'claude-3-5-sonnet',
            ]),
            temperature: faker.number.float({
                min: 0,
                max: 1,
                multipleOf: 0.1,
            }),
            cost: faker.number.float({ min: 0.01, max: 2.0, multipleOf: 0.01 }),
            latency: faker.number.float({
                min: 500,
                max: 5000,
                multipleOf: 100,
            }),
            inference_arguments: ['input_text', 'context'],
        },
        created_at: faker.date.past(),
    });

export const createFakeToolInvocationWithLLMInvocations = ({
    experimentStageId,
    datasetItemId,
    numLlmInvocations = faker.number.int({ min: 1, max: 3 }),
}: {
    experimentStageId?: string;
    datasetItemId?: string;
    numLlmInvocations?: number;
} = {}): ToolInvocationWithToolImplementationAndLLMInvocations => {
    const id = faker.string.uuid();
    return {
        id,
        tool_implementation_id: faker.string.uuid(),
        experiment_stage_id: experimentStageId ?? faker.string.uuid(),
        dataset_item_id: datasetItemId ?? faker.string.uuid(),
        data: {
            input_text: faker.lorem.sentence(),
            context: faker.lorem.paragraph(),
        },
        result: {
            analysis: faker.lorem.sentence(),
            confidence: faker.number.float({
                min: 0.1,
                max: 1.0,
                multipleOf: 0.01,
            }),
        },
        data_hash: faker.string.alphanumeric(32),
        is_cached_result: faker.datatype.boolean(),
        latency_seconds: faker.number.float({
            min: 0.5,
            max: 15,
            multipleOf: 0.1,
        }),
        cost_cents: faker.number.float({
            min: 0.01,
            max: 10.0,
            multipleOf: 0.01,
        }),
        tool_implementation: createFakeUnpopulatedToolImplementation(),
        llm_invocations: Array.from({ length: numLlmInvocations }, () =>
            createFakeLlmInvocation({ toolInvocationId: id }),
        ),
        created_at: faker.date.past(),
        updated_at: faker.date.recent(),
    };
};

export const createFakeExperimentStageInputAndOutputData = ({
    experimentStageId,
}: {
    experimentStageId?: string;
} = {}): ExperimentStageInputAndOutputData => ({
    experiment_stage_id: experimentStageId ?? faker.string.uuid(),
    input_data: {
        sentence: faker.lorem.sentence(),
        context: faker.lorem.paragraph(),
        previous_output: faker.lorem.words(3),
        metadata: {
            source: faker.helpers.arrayElement(['manual', 'api', 'generated']),
            confidence: faker.number.float({
                min: 0.1,
                max: 1.0,
                multipleOf: 0.01,
            }),
        },
    },
    output_data: {
        processed_sentence: faker.lorem.sentence(),
        analysis_result: faker.lorem.words(5),
        confidence_score: faker.number.float({
            min: 0.1,
            max: 1.0,
            multipleOf: 0.01,
        }),
        extracted_entities: Array.from(
            { length: faker.number.int({ min: 1, max: 4 }) },
            () => ({
                text: faker.lorem.word(),
                type: faker.helpers.arrayElement([
                    'PERSON',
                    'LOCATION',
                    'ORGANIZATION',
                    'DATE',
                ]),
            }),
        ),
    },
});

export const createFakeExperimentStageDataWithToolInvocations = ({
    experimentStageId,
    datasetItemId,
    numToolInvocations = faker.number.int({ min: 1, max: 4 }),
}: {
    experimentStageId?: string;
    datasetItemId?: string;
    numToolInvocations?: number;
} = {}): ExperimentStageDataWithToolInvocations => {
    const stageId = experimentStageId ?? faker.string.uuid();
    const stageData = createFakeExperimentStageInputAndOutputData({
        experimentStageId: stageId,
    });
    return {
        ...stageData,
        tool_invocations: Array.from({ length: numToolInvocations }, () =>
            createFakeToolInvocationWithLLMInvocations({
                experimentStageId: stageId,
                datasetItemId,
            }),
        ),
    };
};
